<template>
  <div>
    <card-modal :headerTitle="modalTitle" :icon="icon">
      <template v-slot:body>
        <v-row>
          <v-col>
            <check-box-table-content
              :title="modalTitle"
              :icon="icon"
              :columns="columns"
              :data="data"
              @selected-items="handleSelectedItems"
              :selectLimit="selectLimit"
              :preSelectedItems="preSelectedItems"
            ></check-box-table-content>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <default-date-picker
              label="시작일"
              v-model="newReviewerData.date.startedAt"
            ></default-date-picker>
          </v-col>

        <v-col cols="6">
            <default-date-picker
              label="종료일"
              v-model="newReviewerData.date.endedAt"
            ></default-date-picker>
          </v-col>
        </v-row>
      </template>

      <template v-slot:footer>
        <div class="d-flex justify-end">
          <custom-button
            size="lg"
            color="primary"
            label="등록"
            rounded
            gradient
            @click="handleSaveButton"
          ></custom-button>
          <custom-button
            size="lg"
            color="black"
            label="닫기"
            rounded
            gradient
            @click="closeModal"
          ></custom-button>
        </div>
      </template>
    </card-modal>
  </div>
</template>

<script>
import CardModal from "@/components/common/Modal/CardModal.vue";
import CheckBoxTableContent from "@/components/Tables/CheckBoxTableContent.vue";
import DefaultDatePicker from "@/components/common/Pickers/DefaultDatePicker.vue";
import CustomButton from "@/components/common/buttons/CustomButton.vue";

export default {
  name: "reviewer-select-form",
  components: {
    CardModal,
    CheckBoxTableContent,
    DefaultDatePicker,
    CustomButton,
  },
  props: {
    icon: {
      type: String,
      required: false,
      default: "playlist_add",
    },
    value: {
      type: Array,
      required: true, // 전체 리뷰어 목록
    },
    columns: {
      type: Array,
      required: false,
      default: () => [],
    },
    fields: {
      type: Array,
      required: false,
      default: () => [],
    },
    selectLimit: {
      type: Number,
      required: false,
      default: 0, // 0이면 제한 없음
    },
    // 이미 배정되어 있던 리뷰어들 (review_idx 포함 가능)
    preSelectedItems: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      data: [...this.value],
      modalTitle: "선택하기",
      // 내부 상태
      newReviewerData: {
        date: {
          startedAt: null,
          endedAt: null,
        },
        selectedItems: [],
        addItems: [],
        deleteItems: [],
      },
      // 이전 선택 스냅샷(선택 제한 위반 시 복구용)
      lastSelectedSnapshot: [],
      // 원본(모달 오픈 시점) reviewer_idx 집합
      originalsSet: new Set(),
    };
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },

    // CheckBoxTableContent에서 현재 선택 목록을 내려줌
    handleSelectedItems(selectedItems) {
      // 선택 제한 체크 (0이면 제한 없음)
      if (this.selectLimit > 0 && selectedItems && selectedItems.length > this.selectLimit) {
        // 제한 위반 시 알림 및 복구
        this.notifyVue("top", "center", `최대 ${this.selectLimit}명까지 선택 가능합니다.`);
        // 복구: 부모 테이블에 복구를 지시할 수 없다면 내부 상태만 되돌림
        // (CheckBoxTableContent가 외부에서 내려온 preSelectedItems로만 초기화되고,
        //  선택 상태는 내부에서 관리한다면, 이 부분은 사용자에게 경고만 표시하는 수준으로 둔다)
        return;
      }

      // 선택 상태 갱신
      this.newReviewerData.selectedItems = selectedItems ? [...selectedItems] : [];

      // add/delete 계산
      const currentSet = new Set(this.newReviewerData.selectedItems.map((i) => i.idx));

      // 원래 없었는데 현재는 있다 => add
      const add = this.newReviewerData.selectedItems.filter((i) => !this.originalsSet.has(i.idx));

      // 원래 있었는데 현재는 없다 => delete
      const del = (this.preSelectedItems || []).filter((i) => !currentSet.has(i.idx));

      // 반영
      this.newReviewerData.addItems = add;
      this.newReviewerData.deleteItems = del;

      // 스냅샷 갱신
      this.lastSelectedSnapshot = [...this.newReviewerData.selectedItems];
    },

    sendData() {
      // 상위로 선택/추가/삭제/기간 모두 전달
      this.$emit("send-data", {
        ...this.newReviewerData,
      });
    },

    handleSaveButton() {
      // 최소 선택
      if (!this.newReviewerData.selectedItems || this.newReviewerData.selectedItems.length === 0) {
        this.notifyVue("top", "center", "최소 1명 이상의 심사위원을 선택하세요.");
        return false;
      }

      // 선택 제한 (0 = 무제한)
      if (this.selectLimit > 0 && this.newReviewerData.selectedItems.length > this.selectLimit) {
        this.notifyVue("top", "center", `최대 ${this.selectLimit}명까지 선택 가능합니다.`);
        return false;
      }

      // 기간 검증
      const { startedAt, endedAt } = this.newReviewerData.date || {};
      if (!startedAt || !endedAt) {
        this.notifyVue("top", "center", "기간을 선택하세요.");
        return false;
      }

      const startDate = new Date(startedAt);
      const endDate = new Date(endedAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (endDate < startDate) {
        this.notifyVue("top", "center", "종료일은 시작일 이후로 선택하세요.");
        return false;
      }

      if (endDate < today) {
        this.notifyVue("top", "center", "종료일은 오늘 이후로 선택하세요.");
        return false;
      }

      this.sendData();
    },

    notifyVue(verticalAlign, horizontalAlign, alertMessage) {
      this.$notify({
        timeout: 3000,
        message: alertMessage,
        icon: "priority_high",
        type: "danger",
        horizontalAlign: horizontalAlign,
        verticalAlign: verticalAlign,
      });
    },
  },
  created() {
    // 원본 집합 구성 (모달 오픈 시점의 배정 상태)
    (this.preSelectedItems || []).forEach((it) => this.originalsSet.add(it.idx));

    // 초기 선택 상태 = preSelectedItems
    this.newReviewerData.selectedItems = (this.preSelectedItems || []).map((x) => ({ ...x }));
    this.lastSelectedSnapshot = [...this.newReviewerData.selectedItems];

    // 초깃값에서 add/delete 계산 (보통 둘 다 빈 배열)
    const currentSet = new Set(this.newReviewerData.selectedItems.map((i) => i.idx));
    const add = this.newReviewerData.selectedItems.filter((i) => !this.originalsSet.has(i.idx));
    const del = (this.preSelectedItems || []).filter((i) => !currentSet.has(i.idx));
    this.newReviewerData.addItems = add;
    this.newReviewerData.deleteItems = del;
  },
};
</script>

<style lang="scss" scoped>
$primary-color: #ffac11;

.label {
  color: $primary-color;
  font-weight: 400;
  font-size: medium;
  margin-left: 3px;
}

.field-layout {
  margin: 0;
}

.label-container {
  display: flex;
  flex-direction: row;
  margin-top: 10px;
  margin-bottom: -10px;
}

.table-wrapper {
  min-height: 100px;
  max-height: 400px;
  overflow-y: scroll;
}

.star-icon {
  color: red;
}
</style>
